<template>
  <div class="contract-logs-wrapper">
    <b-card no-body class="mb-0">
      <div class="m-2">
        <!-- Table Top -->
        <div class="d-flex flex-wrap justify-content-between align-items-center mb-3" style="display: none !important;">
          <div class="d-flex align-items-center flex-wrap">
            <h3 class="d-flex align-items-center font-weight-bolder text-nowrap mb-0 mr-3">
              <feather-icon class="mr-50" size="18" icon="ShieldIcon" />
              合約驗證日誌
            </h3>
            <div class="d-flex align-items-center">
              <span class="text-nowrap mr-1">每頁顯示</span>
              <v-select
                v-model="perPage"
                :options="perPageOptions"
                :clearable="false"
                class="per-page-selector"
                style="width: 80px;"
              />
              <span class="text-nowrap ml-1 mr-2">條記錄</span>
            </div>
          </div>
          <div class="d-flex align-items-center flex-wrap">
            <div class="search-box mr-2">
              <feather-icon size="16" icon="SearchIcon" />
              <b-form-input
                v-model="searchQuery"
                placeholder="搜尋..."
                style="width: 200px;"
              />
            </div>
            <b-form-input
              v-model="filterDate"
              type="date"
              class="mr-2"
              placeholder="選擇日期"
              style="width: 150px;"
            />
            <v-select
              v-model="filterLevel"
              :options="levelOptions"
              placeholder="選擇級別"
              class="mr-2"
              style="width: 120px;"
            />
            <b-button variant="outline-secondary" @click="resetFilters">
              <feather-icon size="16" icon="RefreshCwIcon" />
              重置
            </b-button>
          </div>
        </div>

        <!-- 統計卡片 -->
        <b-row class="mt-3 mb-3">
          <b-col lg="3" md="6" sm="12">
            <b-card class="text-center">
              <feather-icon icon="FileTextIcon" size="24" class="text-primary" />
              <h4 class="mb-0 mt-1">{{ stats.total || 0 }}</h4>
              <small class="text-muted">總記錄數</small>
            </b-card>
          </b-col>
          <b-col lg="3" md="6" sm="12">
            <b-card class="text-center">
              <feather-icon icon="InfoIcon" size="24" class="text-success" />
              <h4 class="mb-0 mt-1">{{ (stats.by_level && stats.by_level.info) || 0 }}</h4>
              <small class="text-muted">資訊記錄</small>
            </b-card>
          </b-col>
          <b-col lg="3" md="6" sm="12">
            <b-card class="text-center">
              <feather-icon icon="AlertTriangleIcon" size="24" class="text-warning" />
              <h4 class="mb-0 mt-1">{{ (stats.by_level && stats.by_level.warning) || 0 }}</h4>
              <small class="text-muted">警告記錄</small>
            </b-card>
          </b-col>
          <b-col lg="3" md="6" sm="12">
            <b-card class="text-center">
              <feather-icon icon="XCircleIcon" size="24" class="text-danger" />
              <h4 class="mb-0 mt-1">{{ (stats.by_level && stats.by_level.error) || 0 }}</h4>
              <small class="text-muted">錯誤記錄</small>
            </b-card>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="refContractLogsListTable"
        class="position-relative"
        :items="fetchContractLogs"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        empty-text="未找到匹配的記錄"
        :sort-desc.sync="isSortDirDesc"
      >
        <template #cell(level)="data">
          <b-badge
            :variant="getLevelVariant(data.item.level)"
            class="text-capitalize"
          >
            {{ data.item.level }}
          </b-badge>
        </template>

        <template #cell(timestamp)="data">
          {{ formatDateTime(data.item.timestamp) }}
        </template>

        <template #cell(context)="data">
          <div v-if="data.item.context && Object.keys(data.item.context).length">
            <b-badge
              v-if="data.item.context.contract_number"
              variant="info"
              class="mr-1"
            >
              {{ data.item.context.contract_number }}
            </b-badge>
            <b-badge
              v-if="data.item.context.ip"
              variant="secondary"
              class="mr-1"
            >
              {{ data.item.context.ip }}
            </b-badge>
          </div>
          <span v-else class="text-muted">無</span>
        </template>

        <template #cell(actions)="data">
          <b-button
            variant="outline-info"
            size="sm"
            @click="showLogDetail(data.item)"
          >
            <feather-icon size="16" icon="EyeIcon" />
            詳情
          </b-button>
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">
              在{{ dataMeta.of }}個記錄中，正在顯示 {{ dataMeta.from }} 至 {{ dataMeta.to }}個記錄
            </span>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalContractLogs"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- 日誌詳情彈窗 -->
    <b-modal
      v-model="showDetailModal"
      title="日誌詳情"
      size="lg"
      hide-footer
      scrollable
    >
      <div v-if="selectedLog">
        <b-row>
          <b-col sm="3"><strong>時間：</strong></b-col>
          <b-col sm="9">{{ formatDateTime(selectedLog.timestamp) }}</b-col>
        </b-row>
        <b-row class="mt-2">
          <b-col sm="3"><strong>級別：</strong></b-col>
          <b-col sm="9">
            <b-badge :variant="getLevelVariant(selectedLog.level)">
              {{ selectedLog.level }}
            </b-badge>
          </b-col>
        </b-row>
        <b-row class="mt-2">
          <b-col sm="3"><strong>消息：</strong></b-col>
          <b-col sm="9">{{ selectedLog.message }}</b-col>
        </b-row>
        <b-row v-if="selectedLog.context && Object.keys(selectedLog.context).length" class="mt-2">
          <b-col sm="3"><strong>詳細信息：</strong></b-col>
          <b-col sm="9">
            <pre class="bg-light p-2 rounded">{{ JSON.stringify(selectedLog.context, null, 2) }}</pre>
          </b-col>
        </b-row>
        <b-row class="mt-2">
          <b-col sm="3"><strong>原始記錄：</strong></b-col>
          <b-col sm="9">
            <pre class="bg-light p-2 rounded small">{{ selectedLog.raw }}</pre>
          </b-col>
        </b-row>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BTable,
  BRow,
  BCol,
  BPagination,
  BBadge,
  BFormInput,
  BButton,
  BModal,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import { ref, onUnmounted } from '@vue/composition-api'
import { formatDate } from '@core/utils/filter'
import contractLogsStoreModule from './contractLogsStoreModule'
import useContractLogsList from './useContractLogsList'
import store from '@/store'

export default {
  components: {
    BCard,
    BTable,
    BRow,
    BCol,
    BPagination,
    BBadge,
    BFormInput,
    BButton,
    BModal,
    vSelect,
  },
  setup() {
    const CONTRACT_LOGS_STORE_MODULE_NAME = 'app-contract-logs'

    // Register module
    if (!store.hasModule(CONTRACT_LOGS_STORE_MODULE_NAME)) {
      store.registerModule(CONTRACT_LOGS_STORE_MODULE_NAME, contractLogsStoreModule)
    }

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(CONTRACT_LOGS_STORE_MODULE_NAME)) {
        store.unregisterModule(CONTRACT_LOGS_STORE_MODULE_NAME)
      }
    })

    const {
      fetchContractLogs,
      fetchStats,
      tableColumns,
      perPage,
      currentPage,
      totalContractLogs,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refContractLogsListTable,

      filterDate,
      filterLevel,
      levelOptions,
      resetFilters,

      stats,

      showDetailModal,
      selectedLog,
      showLogDetail,
    } = useContractLogsList()

    // 加載統計數據
    fetchStats()

    return {
      fetchContractLogs,
      tableColumns,
      perPage,
      currentPage,
      totalContractLogs,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refContractLogsListTable,

      filterDate,
      filterLevel,
      levelOptions,
      resetFilters,

      stats,

      showDetailModal,
      selectedLog,
      showLogDetail,

      // Extra functions
      formatDateTime: (date) => {
        if (!date) return '-'
        try {
          const d = new Date(date)
          return d.toLocaleString('zh-TW', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          })
        } catch (error) {
          return date
        }
      },
      getLevelVariant: (level) => {
        const variants = {
          info: 'success',
          warning: 'warning',
          error: 'danger',
        }
        return variants[level] || 'secondary'
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.contract-logs-wrapper {
  max-width: 100%;
  overflow-x: auto;
}

.per-page-selector {
  font-size: 14px;
  min-width: 80px;
  ::v-deep .vs__dropdown-toggle {
    height: 35px;
    padding: 4px 8px;
  }
}

.search-box {
  position: relative;
  .form-control {
    padding-left: 2.2rem;
    height: 35px;
  }
  .feather-icon {
    position: absolute;
    left: 0.6rem;
    top: 50%;
    transform: translateY(-50%);
    z-index: 5;
    color: #6c757d;
  }
}

// 表格樣式優化
::v-deep .table {
  margin-bottom: 0;
  
  th {
    border-top: none;
    font-weight: 600;
    color: #5e5873;
    font-size: 0.857rem;
    white-space: nowrap;
    padding: 0.75rem 0.5rem;
  }
  
  td {
    vertical-align: middle;
    font-size: 0.857rem;
    padding: 0.75rem 0.5rem;
    word-break: break-word;
  }
}

// 表格響應式
::v-deep .table-responsive {
  border: none;
}

// 統計卡片樣式
::v-deep .card {
  border: none;
  box-shadow: 0 4px 25px 0 rgba(230, 230, 230, 0.4);
  
  &.text-center {
    padding: 1rem;
    transition: transform 0.2s;
    
    &:hover {
      transform: translateY(-2px);
    }
  }
}

// v-select 樣式統一
::v-deep .vs__dropdown-toggle {
  border-color: #d8d6de;
  height: 35px;
  
  &:hover {
    border-color: #7367f0;
  }
}

::v-deep .vs__selected {
  font-size: 14px;
}
</style>